import { useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useVirtualizer } from '@tanstack/react-virtual';
import * as R from 'ramda';
import { Asset, Side, SideWithAsset } from 'shared-types';

import SortablePageItem from 'components/Book/SortablePageItem';

import API from 'services/API';

interface ProductSidesPreviewsListProps {
  sides: SideWithAsset[];
  pdfPreviews: Map<string, string>;
  // Scroll-container van AssetsSidebar (de <Row ref={rowRef}>) — de virtualizer moet weten
  // welk DOM-element daadwerkelijk scrolt om de zichtbare range te kunnen bepalen.
  scrollContainerRef: React.RefObject<HTMLDivElement>;
}

// Geschatte hoogte van een niet-gemeten kaart; wordt na de eerste render per item
// gecorrigeerd door de virtualizer (measureElement), dus hoeft niet exact te zijn.
const ESTIMATED_ITEM_HEIGHT = 96;

const getPreviewFile = (side: SideWithAsset, pdfPreviews: Map<string, string>) =>
  side.previewThumbnailUrl && side.previewThumbnailUrl !== ''
    ? side.previewThumbnailUrl
    : side.previewImageUrl && side.previewImageUrl !== ''
      ? side.previewImageUrl
      : pdfPreviews.get(side.id) || side.previewImageUrl;

const ProductSidesPreviewsList = ({
  sides,
  pdfPreviews,
  scrollContainerRef,
}: ProductSidesPreviewsListProps) => {
  const queryClient = useQueryClient();
  const sidesCount = sides.length; // Get the number of sides

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const { mutate: swapPageNumbersMutation } = useMutation(
    'updatePageNumber',
    (params: { sessionId: Asset['sessionId']; sideId: Side['id']; targetSideId: Side['id'] }) =>
      API.swapPageNumbers(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('products');
      },
    },
  );

  const virtualizer = useVirtualizer({
    count: sides.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ESTIMATED_ITEM_HEIGHT,
    overscan: 8,
  });

  // Deze component mount al zodra AssetsSidebar's eigen (losse) assets-query klaar is, terwijl
  // `sides` — afkomstig uit een andere fetch (products) — op dat moment nog leeg kan zijn. De
  // virtualizer initialiseert dan met count 0; als `sides` daarna alsnog gevuld raakt, blijkt de
  // interne range-berekening soms op "leeg" te blijven hangen (totalSize klopt, getVirtualItems()
  // niet). `measure()` forceert een herberekening zodra er weer sides zijn.
  useEffect(() => {
    if (sides.length > 0) virtualizer.measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sides.length]);

  if (R.isEmpty(sides)) return null;

  function handleDragEnd(event: any) {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = sides.findIndex((side) => side.id === active.id);
      const newIndex = sides.findIndex((side) => side.id === over.id);
      const sideId = sides[newIndex].id;
      const targetSideId = sides[oldIndex].id;
      const sessionId = sides[0].asset.sessionId;
      swapPageNumbersMutation({ sessionId, sideId, targetSideId });
    }
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={sides.map((side) => side.id)} strategy={verticalListSortingStrategy}>
        <div style={{ position: 'relative', width: '100%', height: virtualizer.getTotalSize() }}>
          {virtualizer.getVirtualItems().map((virtualItem) => {
            const side = sides[virtualItem.index];
            return (
              <div
                key={side.id}
                ref={virtualizer.measureElement}
                data-index={virtualItem.index}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                <SortablePageItem
                  side={side}
                  index={virtualItem.index}
                  sidesCount={sidesCount}
                  previewFile={getPreviewFile(side, pdfPreviews)}
                />
              </div>
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default ProductSidesPreviewsList;
